/**
 * ComparisonSheetContainer — self-fetching wrapper around `ComparisonSheet`.
 *
 * Use this when you want the 3-way invoice comparison sheet (Mongo / V4 / QBO)
 * outside the dedicated `/acc/invoices` page, e.g. from the AR aging page or
 * any other invoice list. Caller provides only `invoiceNumber` + open/close
 * control; the container handles V4 lookup, Mongo (V3) detail, and QBO detail.
 *
 * Same staleness rules and query keys as the SyncTab inside QboInvoicesPage —
 * so caches are shared and the sheet feels instant on second open.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { ComparisonSheet, type QboInvoiceDetail } from '../pages/QboInvoicesPage';
import type { Invoice, V4Invoice } from '@/features/invoices/types';

const SHEET_STALE_MS = 5 * 60 * 1000;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Invoice number (e.g. "4361"). Pass null/empty to keep sheet closed. */
  invoiceNumber: string | null;
}

export default function ComparisonSheetContainer({ open, onOpenChange, invoiceNumber }: Props) {
  // ── 1. V4 invoice by number — gives us mongoInvoiceId, qbInvoiceId, account info.
  const v4Query = useQuery({
    queryKey: ['atek-invoice-by-number', invoiceNumber],
    queryFn: () => api.get<V4Invoice>(`/v4/invoices/by-number/${encodeURIComponent(invoiceNumber!)}`),
    enabled: !!invoiceNumber && open,
    staleTime: SHEET_STALE_MS,
  });
  const v4 = v4Query.data ?? null;

  // ── 2. Mongo (V3) invoice — needed for SKU breakdown in the sheet.
  const atekDetailQuery = useQuery({
    queryKey: ['atek-invoice-detail', v4?.mongoInvoiceId],
    queryFn: () => api.get<{ invoice: Invoice }>(`/v4/legacy/invoices/${v4!.mongoInvoiceId}`, {
      populate: JSON.stringify(['customer', 'contract', 'sales_manager', 'contractual_manager']),
    }),
    enabled: !!v4?.mongoInvoiceId && open,
    staleTime: SHEET_STALE_MS,
  });

  // ── 3. QBO invoice — direct lookup if we have a quickbooksInvoiceId,
  //      otherwise fall back to docNumber-indexed lookup against the cached
  //      QBO list (same heavy query SyncTab uses, so cache hit is ~free).
  const qboDirectQuery = useQuery({
    queryKey: ['qbo-invoice-detail', v4?.quickbooksInvoiceId],
    queryFn: () => api.get<QboInvoiceDetail>(`/v4/quickbooks/${v4!.quickbooksInvoiceId}`),
    enabled: !!v4?.quickbooksInvoiceId && open,
    staleTime: SHEET_STALE_MS,
  });

  const qboAllQuery = useQuery({
    queryKey: ['quickbooks-invoices-all-for-sync'],
    queryFn: () => api.get<{ invoices: Array<{ id: string; docNumber: string }> }>('/v4/quickbooks'),
    staleTime: SHEET_STALE_MS,
    enabled: open && !!invoiceNumber && !v4?.quickbooksInvoiceId,
  });
  const fallbackQboId = useMemo(() => {
    if (v4?.quickbooksInvoiceId) return null;
    if (!invoiceNumber) return null;
    const m = (qboAllQuery.data?.invoices ?? []).find((i) => i.docNumber === invoiceNumber);
    return m?.id ?? null;
  }, [invoiceNumber, qboAllQuery.data, v4?.quickbooksInvoiceId]);

  const qboFallbackQuery = useQuery({
    queryKey: ['qbo-invoice-detail', fallbackQboId],
    queryFn: () => api.get<QboInvoiceDetail>(`/v4/quickbooks/${fallbackQboId}`),
    enabled: !!fallbackQboId && open,
    staleTime: SHEET_STALE_MS,
  });

  const qboInvoice = qboDirectQuery.data || qboFallbackQuery.data || null;
  const isLoadingQbo = (!!v4?.quickbooksInvoiceId && qboDirectQuery.isLoading)
    || (!!fallbackQboId && qboFallbackQuery.isLoading)
    || (!!invoiceNumber && !v4?.quickbooksInvoiceId && !fallbackQboId && qboAllQuery.isLoading);

  return (
    <ComparisonSheet
      open={open}
      onOpenChange={onOpenChange}
      atekInvoice={atekDetailQuery.data?.invoice ?? null}
      atekV4={v4}
      qboInvoice={qboInvoice}
      isLoadingAtek={(!!v4?.mongoInvoiceId && atekDetailQuery.isLoading) || (!!invoiceNumber && v4Query.isLoading)}
      isLoadingQbo={isLoadingQbo}
      invoiceId={v4?.id || null}
      invoiceNumber={invoiceNumber || ''}
    />
  );
}
